import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { OrganizationRes } from '@dealstack/shared/lib/generated';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { buildOrganizationsTableColumns } from '@dealstack/frontend/app/pages/Organizations/components/OrganizationsTable/organizationsTableColumns.tsx';

interface Props {
	organizations: OrganizationRes[];
	rowCount: number;
	dataOperations: MantineTableDataOperations;
	isLoading: boolean;
	onEdit: (organization: OrganizationRes) => void;
	onDelete: (organization: OrganizationRes) => void;
}

export const OrganizationsTable = ({
	organizations,
	rowCount,
	dataOperations,
	isLoading,
	onEdit,
	onDelete,
}: Props) => {
	const tOrganization = useTranslationOrganization();
	const navigate = useNavigate();

	const columns = useMemo(
		() => buildOrganizationsTableColumns({ tOrganization, onEdit, onDelete }),
		[tOrganization, onEdit, onDelete]
	);

	return (
		<MantineTable<OrganizationRes>
			getRowId={(row) => row.id ?? row.authenticationProviderOrgId ?? row.name}
			columns={columns}
			data={organizations}
			rowCount={rowCount}
			dataOperations={dataOperations}
			isLoading={isLoading}
			enableTopToolbar={false}
			enableFilters={false}
			enableGlobalFilter={false}
			enableRowSelection={false}
			enableRowActions={false}
			enablePagination={true}
			manualPagination={true}
			mantineTableBodyRowProps={({ row }) => ({
				id: row.id,
				style: { cursor: row.original.id ? 'pointer' : 'default' },
				onClick: () => {
					if (!row.original.id) {
						return;
					}
					navigate(`/organizations/${row.original.id}`);
				},
			})}
		/>
	);
};
